import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Swal from 'sweetalert2';

export default function ToGo() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const res = await api.get('/ordenes/para-llevar/activas');
      setOrders(res.data);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    setCustomerName('');
    setCustomerAddress('');
    setShowModal(true);
  };

  const createOrder = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const res = await api.post('/ordenes/para-llevar', {
        nombre_cliente: customerName || null,
        direccion_cliente: customerAddress || null,
      });
      setShowModal(false);
      navigate(`/ordenes/${res.data.id}`);
    } catch {
      Swal.fire('Error', 'Could not create the order', 'error');
    } finally {
      setCreating(false);
    }
  };

  const cancelOrder = async (orderId, e) => {
    e.stopPropagation();
    const result = await Swal.fire({
      title: 'Cancel order?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, cancel',
      cancelButtonText: 'No',
      confirmButtonColor: '#d33',
    });
    if (result.isConfirmed) {
      try {
        await api.delete(`/ordenes/para-llevar/${orderId}`);
        loadOrders();
        Swal.fire('Cancelled', 'Order cancelled successfully', 'success');
      } catch {
        Swal.fire('Error', 'Could not cancel the order', 'error');
      }
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-0">To Go / Delivery</h2>
          <small className="text-muted">
            {orders.length} active order{orders.length !== 1 ? 's' : ''}
          </small>
        </div>
        <button className="btn btn-primary" onClick={openModal}>
          + New Order
        </button>
      </div>

      {orders.length === 0 ? (
        <div className="text-center mt-5">
          <div style={{ fontSize: 64 }}>📦</div>
          <h5 className="text-muted mt-3">No active orders</h5>
          <p className="text-muted">Create a new order to get started</p>
          <button className="btn btn-primary mt-2" onClick={openModal}>
            + New Order
          </button>
        </div>
      ) : (
        <div className="row g-3">
          {orders.map((order) => (
            <div key={order.id} className="col-12 col-md-6 col-lg-4">
              <div
                className="card h-100"
                style={{
                  borderTop: '4px solid #3b82f6',
                  borderRadius: '12px',
                  cursor: 'pointer',
                }}
                onClick={() => navigate(`/ordenes/${order.id}`)}
              >
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">Order #{order.id}</h5>
                    <span className="badge bg-primary">📦 To Go</span>
                  </div>
                  {order.nombre_cliente && (
                    <p className="mb-1" style={{ fontSize: 13, fontWeight: 600 }}>
                      👤 {order.nombre_cliente}
                    </p>
                  )}
                  {order.direccion_cliente && (
                    <p className="text-muted mb-1" style={{ fontSize: 13 }}>
                      📍 {order.direccion_cliente}
                    </p>
                  )}
                  <p className="text-muted mb-1" style={{ fontSize: 13 }}>
                    {order.items?.length || 0} item{(order.items?.length || 0) !== 1 ? 's' : ''}
                  </p>
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                      Total: <strong>${parseFloat(order.total || 0).toFixed(2)}</strong>
                    </p>
                    <button className="btn btn-danger btn-sm" onClick={(e) => cancelOrder(order.id, e)}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New Order Modal */}
      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">📦 New To Go Order</h5>
                <button className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Customer name (optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="E.g.: John Smith"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Address (optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="E.g.: 123 Main St"
                    value={customerAddress}
                    onChange={(e) => setCustomerAddress(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={createOrder} disabled={creating}>
                  {creating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Creating...
                    </>
                  ) : (
                    'Create Order'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}